import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { API, authFetch } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { STAFF } from "../config/staffConfig";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Shield,
  AlertTriangle,
  FolderPlus,
  Send,
  Eraser,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import { obSendWithheldReason } from "../utils/obSendGuard";

/* ── Helpers ── */

function getUserEmail() {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.email) return u.email;
    }
  } catch (e) {}
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    "";
  if (email) return email;
  const uid = localStorage.getItem("bonsai_user_id") || "";
  return uid.includes("@") ? uid : "";
}

// BM-H602: identity comes from the signed session (cookie, or the real user
// id via authFetch). The page no longer sends the email as the user id, which
// the backend used to accept on its word.
function getHeaders() {
  return { "Content-Type": "application/json" };
}

function isDirector() {
  const email = getUserEmail().toLowerCase();
  const staff = STAFF.find((s) => s.email === email);
  return staff?.dashboard_role === "director";
}

function isAdmin() {
  const email = getUserEmail().toLowerCase();
  const staff = STAFF.find((s) => s.email === email);
  return (
    staff?.dashboard_role === "director" ||
    staff?.dashboard_role === "admin" ||
    staff?.is_admin === true
  );
}

async function apiFetch(path, method = "GET", body = null) {
  const opts = { method, headers: getHeaders() };
  if (body) opts.body = JSON.stringify(body);
  const resp = await authFetch(`${API}${path}`, opts);
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `${resp.status} ${resp.statusText}`);
  }
  return resp.json();
}

/* ── Confirmation Modal ── */

function ConfirmModal({ open, onClose, onConfirm, action, targetRef, description, isIrreversible }) {
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const confirmed = typed.trim() === targetRef;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Action failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-navy border-navy-border text-txt max-w-md">
        <DialogHeader>
          <DialogTitle className="text-teal flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Confirm: {action}
          </DialogTitle>
          <DialogDescription className="text-txt-muted">
            {description}
          </DialogDescription>
        </DialogHeader>

        {isIrreversible && (
          <div className="flex items-start gap-2 p-3 rounded bg-status-red/10 border border-status-red/30">
            <AlertTriangle className="w-4 h-4 text-status-red shrink-0 mt-0.5" />
            <p className="text-xs text-status-red">
              This action modifies production data and may be irreversible.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs text-txt-muted">
            Type <span className="font-mono text-teal">{targetRef}</span> to confirm:
          </p>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={targetRef}
            className="bg-navy-card border-navy-border text-txt font-mono text-sm"
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-navy-border text-txt-muted"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!confirmed || loading}
            className={`gap-1.5 ${
              isIrreversible
                ? "bg-status-red hover:bg-status-red/90"
                : "bg-teal hover:bg-teal/90"
            } text-white disabled:opacity-40`}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Dashboard View ── */

function DashboardView({ onSelectQueue }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch("/operator/dashboard");
      setData(result);
    } catch (err) {
      setError(errorMessage(err, "Failed to load operator dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 space-y-3">
        <XCircle className="w-8 h-8 text-status-red mx-auto" />
        <p className="text-sm text-status-red">{error}</p>
        <Button onClick={load} variant="outline" className="gap-1.5 border-navy-border text-txt-muted">
          <RefreshCw className="w-4 h-4" /> Retry
        </Button>
      </div>
    );
  }

  const queues = data?.queues || [];
  const iconMap = {
    stuck_refs: RefreshCw,
    missing_sp_folders: FolderPlus,
    failed_ob_sends: Send,
    ghost_refs: Eraser,
    pending_ob_links: Send,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-txt">Remediation Queues</h2>
          <p className="text-xs text-txt-muted">Operator: {data?.operator}</p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="gap-1.5 border-navy-border text-txt-muted hover:border-teal/50 hover:text-teal"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {queues.map((q) => {
          const Icon = iconMap[q.key] || ClipboardList;
          return (
            <div
              key={q.key}
              className="glass-card p-5 rounded-md border border-navy-border hover:border-teal/40 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-teal" />
                  <h3 className="text-sm font-semibold text-txt">{q.label}</h3>
                </div>
                <Badge
                  variant="outline"
                  className={`text-xs font-mono ${
                    q.count > 0
                      ? "border-status-amber text-status-amber"
                      : "border-teal text-teal"
                  }`}
                >
                  {q.count}
                </Badge>
              </div>
              <p className="text-xs text-txt-muted mb-4 leading-relaxed">
                {q.description}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => onSelectQueue(q.key)}
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs border-navy-border text-txt-muted hover:border-teal/50 hover:text-teal"
                >
                  View
                </Button>
                {q.key === "stuck_refs" && (
                  <RunRetryButton queueKey={q.key} onDone={load} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Run Retry Button (for stuck refs bulk retry) ── */

function RunRetryButton({ queueKey, onDone }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setConfirmOpen(true)}
        size="sm"
        className="gap-1.5 text-xs bg-teal hover:bg-teal/90 text-white"
      >
        <RefreshCw className="w-3.5 h-3.5" /> Run Retry
      </Button>
      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          const result = await apiFetch(
            "/operator/retry-stuck-matter-refs",
            "POST"
          );
          toast.success(
            `Retried: ${result.succeeded} succeeded, ${result.failed} failed out of ${result.scanned} scanned`
          );
          onDone?.();
        }}
        action="Retry Stuck Matter Refs"
        targetRef="RETRY"
        description="This will attempt to generate matter references for all stuck conflict checks."
        isIrreversible={false}
      />
    </>
  );
}

/* ── Queue Detail View ── */

function QueueDetailView({ queueKey, onBack }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageSize = 50;

  const queuePaths = {
    stuck_refs: "/operator/queue/stuck-refs",
    missing_sp_folders: "/operator/queue/missing-sp-folders",
    failed_ob_sends: "/operator/queue/failed-ob-sends",
    ghost_refs: "/operator/queue/ghost-refs",
    pending_ob_links: "/operator/queue/pending-ob-links",
  };

  const queueLabels = {
    stuck_refs: "Stuck Matter Refs",
    missing_sp_folders: "Missing SP Folders",
    failed_ob_sends: "Failed OB Sends",
    ghost_refs: "Ghost Refs",
    pending_ob_links: "Pending OB Links",
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const path = queuePaths[queueKey];
      const result = await apiFetch(`${path}?page=${page}&page_size=${pageSize}`);
      setRecords(result.records || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(errorMessage(err, "Failed to load queue"));
    } finally {
      setLoading(false);
    }
  }, [queueKey, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            onClick={onBack}
            variant="outline"
            size="sm"
            className="gap-1.5 border-navy-border text-txt-muted"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <h2 className="text-lg font-semibold text-txt">
            {queueLabels[queueKey] || queueKey}
          </h2>
          <Badge variant="outline" className="border-navy-border text-txt-muted text-xs">
            {total} records
          </Badge>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="gap-1.5 border-navy-border text-txt-muted hover:border-teal/50 hover:text-teal"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-teal" />
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-sm text-status-red">{error}</p>
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle className="w-8 h-8 text-teal mx-auto mb-2" />
          <p className="text-sm text-txt-muted">Queue is empty</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-border text-left">
                  {queueKey === "stuck_refs" && (
                    <>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Client</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Fee Earner</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Type</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Status</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Created</th>
                    </>
                  )}
                  {queueKey === "missing_sp_folders" && (
                    <>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Client</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Ref</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Fee Earner</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Actions</th>
                    </>
                  )}
                  {queueKey === "failed_ob_sends" && (
                    <>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Client</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Ref</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Email</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Error</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Actions</th>
                    </>
                  )}
                  {queueKey === "ghost_refs" && (
                    <>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Client</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Current Ref</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Fee Earner</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Actions</th>
                    </>
                  )}
                  {queueKey === "pending_ob_links" && (
                    <>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Client</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Ref</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Status</th>
                      <th className="py-2 px-3 text-xs text-txt-muted font-medium">Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <QueueRow
                    key={r.id || r.matter_reference || i}
                    record={r}
                    queueKey={queueKey}
                    onDone={load}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-txt-muted">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  size="sm"
                  variant="outline"
                  className="border-navy-border text-txt-muted"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  size="sm"
                  variant="outline"
                  className="border-navy-border text-txt-muted"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Individual Queue Row with per-record actions ── */

function QueueRow({ record, queueKey, onDone }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState(null);

  const r = record;

  const openAction = (type) => {
    setActionType(type);
    setConfirmOpen(true);
  };

  const runAction = async () => {
    const ref = r.matter_reference;
    if (actionType === "retry-sp-folder") {
      const result = await apiFetch(`/operator/retry-missing-sp-folders/${encodeURIComponent(ref)}`, "POST");
      toast.success(`SP folder ${result.success ? "created" : "attempted"} for ${ref}`);
    } else if (actionType === "resend-ob") {
      await apiFetch(`/operator/resend-ob-link/${encodeURIComponent(ref)}`, "POST");
      toast.success(`OB link resent for ${ref}`);
    } else if (actionType === "sanitise") {
      const result = await apiFetch(`/operator/sanitise-matter-ref/${encodeURIComponent(ref)}`, "POST");
      toast.success(result.changed ? `Sanitised: ${result.old_ref} → ${result.matter_reference}` : "Already clean");
    }
    onDone?.();
  };

  const actionInfo = {
    "retry-sp-folder": {
      label: "Retry SP Folder",
      description: `Create SharePoint folder for ${r.matter_reference}. Safe to retry — uses conflictBehavior: fail.`,
      irreversible: false,
    },
    "resend-ob": {
      label: "Resend OB Link",
      description: `Resend onboarding invitation email for ${r.matter_reference}.`,
      irreversible: false,
    },
    "sanitise": {
      label: "Sanitise Ref",
      description: `Clean malformed reference "${r.matter_reference}". This will update the reference across conflict checks and onboarding records.`,
      irreversible: true,
    },
  };

  const info = actionInfo[actionType] || {};

  return (
    <>
      <tr className="border-b border-navy-border/50 hover:bg-navy-hover/30">
        {queueKey === "stuck_refs" && (
          <>
            <td className="py-2.5 px-3 text-txt">{r.client_name || "—"}</td>
            <td className="py-2.5 px-3 text-txt-muted">{r.fee_earner || "—"}</td>
            <td className="py-2.5 px-3 text-txt-muted">{r.matter_type || "—"}</td>
            <td className="py-2.5 px-3">
              <Badge variant="outline" className="text-xs border-status-amber/50 text-status-amber">
                {r.conflict_status}
              </Badge>
            </td>
            <td className="py-2.5 px-3 text-txt-muted text-xs">
              {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
            </td>
          </>
        )}
        {queueKey === "missing_sp_folders" && (
          <>
            <td className="py-2.5 px-3 text-txt">{r.client_name || "—"}</td>
            <td className="py-2.5 px-3 font-mono text-xs text-teal">{r.matter_reference}</td>
            <td className="py-2.5 px-3 text-txt-muted">{r.fee_earner || "—"}</td>
            <td className="py-2.5 px-3">
              <Button
                onClick={() => openAction("retry-sp-folder")}
                size="sm"
                className="gap-1 text-xs bg-teal hover:bg-teal/90 text-white"
              >
                <FolderPlus className="w-3.5 h-3.5" /> Create Folder
              </Button>
            </td>
          </>
        )}
        {queueKey === "failed_ob_sends" && (
          <>
            <td className="py-2.5 px-3 text-txt">{r.client_name || "—"}</td>
            <td className="py-2.5 px-3 font-mono text-xs text-teal">{r.matter_reference || "—"}</td>
            <td className="py-2.5 px-3 text-txt-muted text-xs">{r.client_email || "—"}</td>
            <td className="py-2.5 px-3 text-xs text-status-red max-w-[200px] truncate">
              {r.email_error || "—"}
            </td>
            <td className="py-2.5 px-3">
              {r.matter_reference && (
                <Button
                  onClick={() => openAction("resend-ob")}
                  size="sm"
                  className="gap-1 text-xs bg-teal hover:bg-teal/90 text-white"
                >
                  <Send className="w-3.5 h-3.5" /> Resend
                </Button>
              )}
            </td>
          </>
        )}
        {queueKey === "ghost_refs" && (
          <>
            <td className="py-2.5 px-3 text-txt">{r.client_name || "—"}</td>
            <td className="py-2.5 px-3 font-mono text-xs text-status-amber">{r.matter_reference}</td>
            <td className="py-2.5 px-3 text-txt-muted">{r.fee_earner || "—"}</td>
            <td className="py-2.5 px-3">
              <Button
                onClick={() => openAction("sanitise")}
                size="sm"
                className="gap-1 text-xs bg-status-amber/80 hover:bg-status-amber text-white"
              >
                <Eraser className="w-3.5 h-3.5" /> Sanitise
              </Button>
            </td>
          </>
        )}
        {queueKey === "pending_ob_links" && (
          <>
            <td className="py-2.5 px-3 text-txt">{r.client_name || "—"}</td>
            <td className="py-2.5 px-3 font-mono text-xs text-teal">{r.matter_reference || "—"}</td>
            <td className="py-2.5 px-3">
              <Badge variant="outline" className="text-xs border-navy-border text-txt-muted">
                {r.status || "—"}
              </Badge>
            </td>
            <td className="py-2.5 px-3">
              {/* BM-H278 (Vanessa, 4 Sep 2026): this queue only excluded the
                  status "completed", which this codebase never writes, so a
                  complete, skipped, linked or returned-offline matter with no
                  ob_link was listed here with a Send OB button. The queue
                  itself now filters those rows out; this is the belt to that
                  braces, and states the OB state if one slips through. */}
              {obSendWithheldReason(r) ? (
                <span className="text-xs text-teal" data-testid="operator-ob-satisfied">
                  {obSendWithheldReason(r)}
                </span>
              ) : r.matter_reference ? (
                <Button
                  onClick={() => openAction("resend-ob")}
                  size="sm"
                  className="gap-1 text-xs bg-teal hover:bg-teal/90 text-white"
                >
                  <Send className="w-3.5 h-3.5" /> Send OB
                </Button>
              ) : null}
            </td>
          </>
        )}
      </tr>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={runAction}
        action={info.label || "Action"}
        targetRef={r.matter_reference || "CONFIRM"}
        description={info.description || ""}
        isIrreversible={info.irreversible || false}
      />
    </>
  );
}

/* ── Audit Log View ── */

function AuditLogView() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch(
        `/operator/audit-log?page=${page}&page_size=${pageSize}`
      );
      setRecords(result.records || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(errorMessage(err, "Failed to load audit log"));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-txt">Audit Log</h2>
        <Button
          onClick={load}
          variant="outline"
          className="gap-1.5 border-navy-border text-txt-muted hover:border-teal/50 hover:text-teal"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-teal" />
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-sm text-status-red">{error}</p>
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-12">
          <ClipboardList className="w-8 h-8 text-txt-muted mx-auto mb-2" />
          <p className="text-sm text-txt-muted">No audit log entries yet</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-border text-left">
                  <th className="py-2 px-3 text-xs text-txt-muted font-medium">Timestamp</th>
                  <th className="py-2 px-3 text-xs text-txt-muted font-medium">Operator</th>
                  <th className="py-2 px-3 text-xs text-txt-muted font-medium">Action</th>
                  <th className="py-2 px-3 text-xs text-txt-muted font-medium">Target</th>
                  <th className="py-2 px-3 text-xs text-txt-muted font-medium">Result</th>
                  <th className="py-2 px-3 text-xs text-txt-muted font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={i} className="border-b border-navy-border/50 hover:bg-navy-hover/30">
                    <td className="py-2 px-3 text-xs text-txt-muted whitespace-nowrap">
                      {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                    </td>
                    <td className="py-2 px-3 text-xs text-txt">{r.operator_email || "—"}</td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="text-xs border-navy-border text-teal">
                        {r.action}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 font-mono text-xs text-txt">{r.target_ref || "—"}</td>
                    <td className="py-2 px-3">
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          r.result === "completed" || r.result === "no_change"
                            ? "border-teal/50 text-teal"
                            : r.result === "error"
                            ? "border-status-red/50 text-status-red"
                            : "border-status-amber/50 text-status-amber"
                        }`}
                      >
                        {r.result}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-xs text-txt-muted max-w-[200px] truncate">
                      {r.detail || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-txt-muted">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  size="sm"
                  variant="outline"
                  className="border-navy-border text-txt-muted"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  size="sm"
                  variant="outline"
                  className="border-navy-border text-txt-muted"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Main OperatorConsole Page ── */

export default function OperatorConsole() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard"); // dashboard | queue | audit
  const [selectedQueue, setSelectedQueue] = useState(null);

  const handleSelectQueue = (queueKey) => {
    setSelectedQueue(queueKey);
    setTab("queue");
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-center space-y-3">
          <Shield className="w-12 h-12 text-status-red mx-auto" />
          <h2 className="text-lg font-semibold text-txt">Access Denied</h2>
          <p className="text-sm text-txt-muted">
            The Operator Console is restricted to directors and admin staff.
          </p>
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="border-navy-border text-txt-muted">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy">
      {/* Top bar */}
      <nav className="glass-nav sticky top-0 z-50 w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-teal flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-teal leading-tight">Operator Console</h1>
                <p className="text-[10px] text-txt-muted tracking-wide font-medium leading-tight">
                  Self-serve remediation
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Tab selector */}
              <div className="flex bg-navy-card border border-navy-border rounded-md p-0.5">
                <button
                  onClick={() => { setTab("dashboard"); setSelectedQueue(null); }}
                  className={`px-3 py-1.5 text-xs rounded transition-colors ${
                    tab === "dashboard" || tab === "queue"
                      ? "bg-teal text-white"
                      : "text-txt-muted hover:text-txt"
                  }`}
                >
                  <LayoutDashboard className="w-3.5 h-3.5 inline mr-1" />
                  Queues
                </button>
                <button
                  onClick={() => { setTab("audit"); setSelectedQueue(null); }}
                  className={`px-3 py-1.5 text-xs rounded transition-colors ${
                    tab === "audit"
                      ? "bg-teal text-white"
                      : "text-txt-muted hover:text-txt"
                  }`}
                >
                  <ClipboardList className="w-3.5 h-3.5 inline mr-1" />
                  Audit Log
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-txt-muted border-navy-border/40 hover:border-teal/50 hover:text-teal gap-1.5"
              >
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === "dashboard" && (
          <DashboardView onSelectQueue={handleSelectQueue} />
        )}
        {tab === "queue" && selectedQueue && (
          <QueueDetailView
            queueKey={selectedQueue}
            onBack={() => { setTab("dashboard"); setSelectedQueue(null); }}
          />
        )}
        {tab === "audit" && <AuditLogView />}
      </main>

      <p className="text-xs text-txt-muted text-center pb-4">
        Logged in as {getUserEmail()}
      </p>
    </div>
  );
}
